import React, { useCallback, useRef } from 'react'
import { GRC_PLATFORM } from '@trustero/trustero-api-web/lib/common/grc_platform_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useConfirmationModal } from './useConfirmationModal'
import { ImportedRecordModalBody } from './ModalForm.components'

export const useImportedRecordModal = <T extends unknown[]>({
  modelType,
  grcPlatform,
  showModal,
  onUpdate,
  dismissWarningFunction,
  onCancel,
}: {
  modelType: MODEL_TYPE
  onUpdate: (...args: T) => Promise<void>
  dismissWarningFunction: () => Promise<void>
  showModal?: boolean
  grcPlatform?: GRC_PLATFORM
  onCancel?: () => void
}): ((...args: T) => void) => {
  const dismissWarning = useRef(false)

  const onConfirmCB = useCallback(
    async (...args: T) => {
      if (dismissWarning.current) {
        await dismissWarningFunction()
      }
      await onUpdate(...args)
      dismissWarning.current = false
    },
    [dismissWarning, dismissWarningFunction, onUpdate],
  )

  const setDismissWarning = useCallback((value: boolean) => {
    dismissWarning.current = value
  }, [])

  const confirmationModal = useConfirmationModal({
    title: 'This change may be lost',
    body: grcPlatform ? (
      <ImportedRecordModalBody
        modelType={modelType}
        grcPlatform={grcPlatform}
        dismissWarning={dismissWarning}
        setDismissWarning={setDismissWarning}
      />
    ) : (
      <></>
    ),
    confirmText: "Okay, that's fine",
    onConfirmCB,
    onCancel: () => {
      onCancel?.()
      dismissWarning.current = false
    },
  })

  return grcPlatform && showModal ? confirmationModal : onUpdate
}
